import { StatusChangeModal } from "@senciamatch/frontend/components/StatusChangeModal";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { WorkShiftGroupStatus } from "@senciamatch/frontend/utils/workShiftGroupStatus";
import { canChangeStatus, getStatusLabel } from "@senciamatch/frontend/utils/workShiftGroupStatus";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export const MatchingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<{ id: string; status: WorkShiftGroupStatus } | null>(null);

  const getWorkShiftGroups = trpc.getWorkShiftGroups.useQuery();
  const updateStatus = trpc.updateWorkShiftGroupStatus.useMutation({
    onSuccess: () => {
      getWorkShiftGroups.refetch();
    },
  });

  if (getWorkShiftGroups.isPending) return <div>Loading...</div>;
  if (!getWorkShiftGroups.data) return <div>No work shift groups found</div>;

  const handleStatusClick = (id: string, status: WorkShiftGroupStatus) => {
    if (canChangeStatus(status)) {
      setSelectedGroup({ id, status });
      setShowStatusModal(true);
    }
  };

  const handleStatusChange = (newStatus: WorkShiftGroupStatus) => {
    if (selectedGroup) {
      updateStatus.mutate({ id: selectedGroup.id, status: newStatus });
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <h2 className="text-2xl font-bold py-10 flex-shrink-0">シフト一覧</h2>
      <div className="overflow-x-auto py-10 bg-white border border-base-100 px-8 flex-grow flex flex-col">
        <div className="flex justify-between pb-8 flex-shrink-0">
          {/* <input type="text" className="w-1/2 border border-gray-200 rounded-md px-4 py-2" placeholder="検索" /> */}
          <button
            type="button"
            className="bg-primary-700 text-white px-4 py-2 rounded-md"
            onClick={() => navigate("/matchings/new")}
          >
            新規作成
          </button>
        </div>
        <div className="overflow-y-auto flex-grow">
          <table className="min-w-full">
            <thead className="sticky top-0 bg-gray-100">
              <tr className="bg-gray-100">
                <th className="p-2 text-left border border-gray-200">名前</th>
                <th className="p-2 text-left border border-gray-200">対象年月</th>
                <th className="p-2 text-left border border-gray-200">ステータス</th>
                <th className="p-2 text-left border border-gray-200">作成日時</th>
                <th className="p-2 text-left border border-gray-200">操作</th>
              </tr>
            </thead>
            <tbody>
              {getWorkShiftGroups.data.workShiftGroups?.map((workShiftGroup) => {
                const status = workShiftGroup.status as WorkShiftGroupStatus;
                const canChange = canChangeStatus(status);
                return (
                  <tr key={workShiftGroup.id}>
                    <td className="p-2 text-left border border-gray-200">{workShiftGroup.name}</td>
                    <td className="p-2 text-left border border-gray-200">
                      {workShiftGroup.year}/{workShiftGroup.month}
                    </td>
                    <td className="p-2 text-left border border-gray-200">
                      <button
                        type="button"
                        onClick={() => handleStatusClick(workShiftGroup.id, status)}
                        className={`${
                          canChange ? "text-blue-600 hover:underline cursor-pointer" : "text-gray-700 cursor-default"
                        }`}
                        disabled={!canChange}
                      >
                        {getStatusLabel(status)}
                      </button>
                    </td>
                    <td className="p-2 text-left border border-gray-200">
                      {`${new Date(workShiftGroup.createdAt)
                        .toLocaleString("ja-JP", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          formatMatcher: "basic",
                        })
                        .replace(/\//g, "年")
                        .replace(/年(\d{2})年/, "年$1月")
                        .replace(/月(\d{2})/, "月$1日 ")
                        .replace(/:/, "時")}分`}
                    </td>
                    <td className="p-2 text-left border border-gray-200">
                      <Link to={`/matchings/${workShiftGroup.id}`} className="text-primary-700">
                        編集
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {selectedGroup && (
        <StatusChangeModal
          show={showStatusModal}
          currentStatus={selectedGroup.status}
          onClose={() => setShowStatusModal(false)}
          onConfirm={handleStatusChange}
        />
      )}
    </div>
  );
};
